import React from 'react';
import { RiCloseLine } from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/Drawer';
import { formatPrice } from '@/utils';
import { getAircraftModelName } from '@/features/tails/utils';
import { usePlatformFeeRate } from '@/hooks/usePlatformFeeRate';
import {
  formatPlatformFeePercent,
  getEffectivePercentFromAmount,
} from '@/utils/platformFee';

/**
 * PriceBreakdownDrawer Component
 * Shows per-aircraft price breakdown in a right drawer
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {Array} props.flightOptions - Array of bid options with price data
 */
const PriceBreakdownDrawer = ({ isOpen, onClose, flightOptions: flightOptionsProp }) => {
  const flightOptions = flightOptionsProp || [];
  // Fallback percent for historical rows whose subtotal is zero (would
  // otherwise divide-by-zero) or for freshly-added options that haven't
  // been priced yet. resolveForSubtotal is bracket-aware.
  const { percent: platformFeePercent, resolveForSubtotal } = usePlatformFeeRate();
  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent side="right" className="w-full !max-w-2xl flex flex-col">
        <DrawerHeader>
          <DrawerTitle className="text-xl">Price Breakdown</DrawerTitle>
        </DrawerHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-2 pb-8">
          <div className="space-y-4">
            {flightOptions.map((option, index) => {
              // Prefer saved operator_cost; fall back to total_price for first-load
              // (before any edit has been saved, total_price IS the operator cost).
              const savedOpCost = parseFloat(option.operator_cost);
              const operatorCost = !isNaN(savedOpCost) && savedOpCost > 0
                ? savedOpCost
                : (parseFloat(option.total_price) || 0);
              const brokerMarkup = parseFloat(option.broker_markup) || 0;
              const fet = parseFloat(option.federal_excise_tax) || 0;
              const segmentTax = parseFloat(option.segment_tax) || 0;
              const intlFacilitiesTax = parseFloat(option.international_tax) || 0;
              const platformFee = parseFloat(option.platform_fee) || 0;
              // Trip flags — legacy rows without the flag default to
              // Domestic-only, matching how the drawer seeds them. FET
              // and Segment Tax are Domestic-only; Intl Facilities Tax
              // only shows when International is on.
              const optionIsDomestic = option.is_domestic !== false;
              const optionIsInternational = !!option.is_international;
              const totalCost = operatorCost + brokerMarkup + fet + segmentTax + intlFacilitiesTax + platformFee;

              const aircraftName = getAircraftModelName(option.aircraft) || `Aircraft ${index + 1}`;

              // Historical accuracy: derive the effective percent from the
              // persisted platform_fee AMOUNT so a saved row keeps showing
              // the rate it was priced at, even after the org rebalances
              // brackets. Only trust the derivation when it lands in a
              // plausible range (0.1% – 20%); rows loaded before the
              // operator_cost / broker_markup are populated (Average Price
              // in quick-quote, etc.) produce a subtotal near zero, which
              // makes the division blow up into obviously-wrong values
              // like "44.35%". Fall back to the current bracket resolver
              // in that case so the label reads the real rate the fee was
              // priced at.
              const subtotal = operatorCost + brokerMarkup;
              const derivedPercent = getEffectivePercentFromAmount(platformFee, subtotal);
              const derivedIsPlausible = derivedPercent != null
                && derivedPercent >= 0.1
                && derivedPercent <= 20;
              const effectivePercent = derivedIsPlausible
                ? derivedPercent
                : (resolveForSubtotal(subtotal).percent || platformFeePercent);

              return (
                <div
                  key={option.bid_option_id || option.aircraft_id || index}
                  className="border border-gray-200 rounded-xl p-5"
                >
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    {aircraftName}
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Operator Cost</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(operatorCost)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Broker Margin</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(brokerMarkup)}</span>
                    </div>

                    {optionIsDomestic && fet > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Federal Excise Tax (7.5%)</span>
                        <span className="text-sm font-medium text-gray-900">{formatPrice(fet)}</span>
                      </div>
                    )}

                    {optionIsDomestic && segmentTax > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Segment Tax</span>
                        <span className="text-sm font-medium text-gray-900">{formatPrice(segmentTax)}</span>
                      </div>
                    )}

                    {optionIsInternational && intlFacilitiesTax > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">International Facilities Tax</span>
                        <span className="text-sm font-medium text-gray-900">{formatPrice(intlFacilitiesTax)}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">TrustJet Fee ({formatPlatformFeePercent(effectivePercent)}%)</span>
                      <span className="text-sm font-medium text-gray-900">{formatPrice(platformFee)}</span>
                    </div>

                    <div className="border-t border-gray-200 pt-3 mt-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">Total Cost</span>
                        <span className="text-sm text-gray-900 font-medium">{formatPrice(totalCost)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Card fee note */}
          {flightOptions.length > 0 && (
            <div className="mt-4 bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-500">
                If client chooses to pay by card, a 3% card fee will be added on top of the total cost.
              </p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default PriceBreakdownDrawer;
